import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Chip } from '@/components/Chip'
import { GridTable } from '@/components/GridTable'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type SecuredValue = components['schemas']['SecuredValue']
type OrganisationUser = components['schemas']['OrganisationUser']

function rowKey(v: SecuredValue): string {
  return `${v.scope}:${v.scope_ref ?? ''}:${v.variable_key}`
}

/**
 * `v-pending` — process/09-SCREEN-SPECS/v-pending.md. **No secret value is ever displayed** (rule
 * 2) — `SecuredValue` carries no value field at all, only `variable_key`/`blocks`/`scope`. Ticking
 * is a HUMAN ASSERTION the product cannot verify (rule 1), so the checkbox is labelled as one, and
 * every tick is audited server-side with the asserting identity.
 *
 * **`organisationListUsers` is not in this screen's own Operations table (a spec-authoring gap,
 * same shape as `v-dashboard`'s W153 finding) — added here as the same deliberate exception**: the
 * operation already exists and is already built (`v-users`'s own member list), so "Assign owner"
 * is a real `<select>` over the organisation's members rather than a raw user-id prompt (W154).
 *
 * **`?job_id=` (W153) narrows to one restore's own rows** — `restoreListSecuredValues`'s own
 * `job_id` query param, "omit for the workspace's whole outstanding list, which is the worklist
 * proper." `v-restorejob`'s own outstanding-values link is this screen's first real caller.
 *
 * **Bulk owner assignment built 2026-09-29 (OI-C57, Sanket's own call).** `restoreAssignSecuredValueOwners`
 * now exists (`POST .../secured-values/owners`), so the bottom "Assign owners" button is real rather
 * than permanently disabled. Row selection reuses `GridTable`'s own existing `selection` prop (a
 * leading checkbox column plus header "select all") rather than a bespoke mechanism — this screen is
 * simply its first caller. The owner `<select>` next to the button is the same organisation-members
 * source and same rendering as the per-row one; both share the single `securedValue.update` action id
 * (`ActionId.yaml` has no bulk-specific member, and the per-row "I have set this value" checkbox
 * already reuses this same id for a different control under the same conceptual action, so this
 * follows established precedent rather than inventing a new registered id for one button). A
 * non-member `owner_user_id` refuses the whole call (`422 VALIDATION_FAILED`) — surfaced the same way
 * `VNotify.tsx`'s `addRecipient` already does for its own mutation: `problem.title` in a plain `Note`,
 * no bespoke error UI.
 */
export function VPending() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const jobId = searchParams.get('job_id') ?? undefined
  const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(new Set())
  const [bulkOwnerUserId, setBulkOwnerUserId] = useState('')

  const values = useQuery({
    queryKey: ['secured-values', orgId, workspaceId, jobId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/secured-values', { params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { job_id: jobId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly SecuredValue[]
    },
  })

  const orgUsers = useQuery({
    queryKey: ['org-users', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/users', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly OrganisationUser[]
    },
  })

  const update = useMutation({
    mutationFn: async (body: components['schemas']['SecuredValueUpdate']) => {
      const client = getApiClient()
      const response = await client.PATCH('/orgs/{org_id}/workspaces/{workspace_id}/secured-values', { params: { path: { org_id: orgId, workspace_id: workspaceId } }, body })
      return unwrap(response as { data?: SecuredValue; error?: Problem })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['secured-values', orgId, workspaceId] }),
  })

  const bulkAssign = useMutation({
    mutationFn: async (body: components['schemas']['SecuredValueOwnerBulkAssign']) => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/secured-values/owners', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body,
      })
      return unwrap(response as { data?: components['schemas']['SecuredValueOwnerBulkAssignResult']; error?: Problem })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secured-values', orgId, workspaceId] })
      setSelectedKeys(new Set())
      setBulkOwnerUserId('')
    },
  })

  const exportRunbook = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/secured-values/export', { params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { job_id: jobId } }, parseAs: 'blob' })
      if (response.error) throw new ApiProblemError(response.error as Problem)
      const url = URL.createObjectURL(response.data as unknown as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'secured-values-runbook.csv'
      a.click()
      URL.revokeObjectURL(url)
    },
  })

  const problem = values.error instanceof ApiProblemError ? values.error.problem : undefined
  const rows = values.data ?? []
  const outstanding = rows.filter((v) => !v.is_set).length
  const reentered = rows.length - outstanding

  return (
    <div data-scr={unitId('v-pending')}>
      {jobId && (
        <Note>
          <a href={`/jobs/restore/${jobId}`}>‹ Restore job {jobId}</a>
        </Note>
      )}
      <div data-cmp="panel-header" className="flex items-center gap-5 mb-6 mt-2">
        <h1 className="m-0 text-section font-semibold flex-1">Values needing re-entry</h1>
        {outstanding > 0 && <Chip variant="plain">{outstanding} outstanding</Chip>}
        <div data-cmp="grid-tools">
          <Note>
            Restore {jobId} · {rows.length} values
          </Note>
          <Button data-act={actionId('securedValues.export')} ghost disabled={exportRunbook.isPending} onClick={() => exportRunbook.mutate()}>
            Export as runbook
          </Button>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 h-2 bg-row rounded-1 overflow-hidden">
            <div className="h-full bg-ok" style={{ width: `${(reentered / rows.length) * 100}%` }} />
          </div>
          <Note>
            {reentered} of {rows.length} re-entered · these pipelines fail until the rest are set
          </Note>
        </div>
      )}

      <Note>We cannot read or verify a secured value — Bitbucket never returns one. Ticking a row records that a person set it, not that the product confirmed it.</Note>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      {!problem && rows.length === 0 && !values.isLoading && <Note>Nothing needs re-entry.</Note>}

      {rows.length > 0 && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mt-6">
          <GridTable
            columns={[
              { key: 'variable', header: 'Variable', render: (v) => <span className="font-mono">{v.variable_key}</span> },
              {
                key: 'scope',
                header: 'Scope',
                render: (v) => (
                  <span className="text-muted">
                    {v.scope}
                    {v.scope_ref ? ` · ${v.scope_ref}` : ''}
                  </span>
                ),
              },
              { key: 'blocks', header: 'Blocks', render: (v) => v.blocks },
              {
                key: 'owner',
                header: 'Owner',
                render: (v) => (
                  <select
                    data-act={actionId('securedValue.update')}
                    value={v.owner?.user_id ?? ''}
                    disabled={update.isPending}
                    onChange={(e) => update.mutate({ scope: v.scope, scope_ref: v.scope_ref, variable_key: v.variable_key, owner_user_id: e.target.value || null })}
                    aria-label={`Owner for ${v.variable_key}`}
                    className="border border-bd2 rounded-1 py-2 px-3 text-small"
                  >
                    <option value="">Unassigned</option>
                    {v.owner && !orgUsers.data?.some((u) => u.user_id === v.owner!.user_id) && (
                      <option value={v.owner.user_id}>{v.owner.display_name ?? v.owner.user_id}</option>
                    )}
                    {(orgUsers.data ?? []).map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.display_name ?? u.email}
                      </option>
                    ))}
                  </select>
                ),
              },
              { key: 'state', header: 'State', render: (v) => <Chip variant={v.is_set ? 'active' : 'failure'}>{v.is_set ? 'Set' : 'Empty'}</Chip> },
              {
                key: 'set',
                header: 'Set',
                render: (v) => (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      data-act={actionId('securedValue.update')}
                      checked={v.is_set}
                      disabled={update.isPending}
                      onChange={(e) => update.mutate({ scope: v.scope, scope_ref: v.scope_ref, variable_key: v.variable_key, is_set: e.target.checked })}
                    />
                    I have set this value
                  </label>
                ),
              },
            ]}
            rows={rows}
            getRowKey={rowKey}
            selection={{
              selectedKeys,
              onToggle: (key) =>
                setSelectedKeys((prev) => {
                  const next = new Set(prev)
                  if (next.has(key)) next.delete(key)
                  else next.add(key)
                  return next
                }),
              onToggleAll: (checked) => setSelectedKeys(checked ? new Set(rows.map(rowKey)) : new Set()),
            }}
          />
          <div data-cmp="grid-footer" className="border-t border-bd p-5 flex items-center justify-between gap-4">
            <Note>
              We never see these values, and we never will. Bitbucket does not return them at any permission level, so
              there is nothing for us to store or restore. Ticking a row records that a human set it in Bitbucket — it
              does not verify the value. Every tick is in the audit log.
            </Note>
            <div className="flex items-center gap-3">
              {bulkAssign.error instanceof ApiProblemError && <Note>{bulkAssign.error.problem.title}</Note>}
              <select
                data-act={actionId('securedValue.update')}
                value={bulkOwnerUserId}
                disabled={selectedKeys.size === 0 || bulkAssign.isPending}
                onChange={(e) => setBulkOwnerUserId(e.target.value)}
                aria-label="Owner to assign to the selected rows"
                className="border border-bd2 rounded-1 py-2 px-3 text-small"
              >
                <option value="">Unassigned</option>
                {(orgUsers.data ?? []).map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.display_name ?? u.email}
                  </option>
                ))}
              </select>
              <Button
                data-act={actionId('securedValue.update')}
                ghost
                disabled={selectedKeys.size === 0 || bulkAssign.isPending}
                title={selectedKeys.size === 0 ? 'Select one or more rows above first' : undefined}
                onClick={() => {
                  const items = rows.filter((v) => selectedKeys.has(rowKey(v))).map((v) => ({ scope: v.scope, scope_ref: v.scope_ref, variable_key: v.variable_key }))
                  bulkAssign.mutate({ items, owner_user_id: bulkOwnerUserId || null })
                }}
              >
                Assign owners{selectedKeys.size > 0 ? ` (${selectedKeys.size})` : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <Note>
          The job's status stays <i>Partial success</i> and does not become <i>Success</i> — an outstanding secret is
          unfinished recovery, and the status should say so rather than reading green while pipelines fail.
        </Note>
      )}
    </div>
  )
}
